import { z } from 'zod';
import {
  JOY_JOB_STATUSES,
  JOY_MAX_MESSAGE_CHARS,
  JOY_SCHEDULE_KINDS,
  JOY_WATCH_KINDS,
  parseTime,
} from './joy.js';

/** مخططات جوي — ما يكتبه المدير، وما يطلبه النموذج من أدواته. */

export const SendJoyMessageSchema = z.object({
  text: z
    .string()
    .trim()
    .min(1, 'اكتب رسالة.')
    .max(JOY_MAX_MESSAGE_CHARS, `الرسالة أطول من ${JOY_MAX_MESSAGE_CHARS} حرفًا.`),
});
export type SendJoyMessageInput = z.infer<typeof SendJoyMessageSchema>;

const time = z
  .string()
  .trim()
  .refine((v) => parseTime(v) !== null, 'الوقت بصيغة ساعة:دقيقة، مثل 09:00.');

/**
 * مهمّةٌ مجدولة.
 *
 * كلُّ نوعٍ يطلب ما يحدّده ولا يُقبل ناقصًا: «كلَّ أسبوع» بلا يومٍ تُنفَّذ
 * يوم الأحد صامتةً، ويظنّ المدير أنّها لا تعمل.
 */
export const ScheduleJoyTaskSchema = z
  .object({
    title: z.string().trim().min(2, 'عنوان المهمّة قصير.').max(120),
    prompt: z.string().trim().min(5, 'ما المطلوب في الموعد؟').max(2000),
    kind: z.enum(JOY_SCHEDULE_KINDS),
    time,
    weekday: z.number().int().min(0).max(6).nullable().optional(),
    dayOfMonth: z.number().int().min(1).max(28).nullable().optional(),
    date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ بصيغة سنة-شهر-يوم.')
      .nullable()
      .optional(),
    alertOnly: z.boolean().default(false),
  })
  .refine((v) => v.kind !== 'WEEKLY' || typeof v.weekday === 'number', {
    message: 'المهمّة الأسبوعية تحتاج يومًا.',
    path: ['weekday'],
  })
  .refine((v) => v.kind !== 'MONTHLY' || typeof v.dayOfMonth === 'number', {
    message: 'المهمّة الشهرية تحتاج يومًا من الشهر.',
    path: ['dayOfMonth'],
  })
  .refine((v) => v.kind !== 'ONCE' || Boolean(v.date), {
    message: 'المهمّة لمرّةٍ واحدة تحتاج تاريخًا.',
    path: ['date'],
  });
export type ScheduleJoyTaskInput = z.infer<typeof ScheduleJoyTaskSchema>;

export const JoyWatchParamsSchema = z
  .object({
    threshold: z.number().int().min(1).max(1000).optional(),
    days: z.number().int().min(1).max(365).optional(),
    minutes: z.number().int().min(10).max(600).optional(),
    time: time.optional(),
    weekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7).optional(),
  })
  .strict();

export const CreateJoyWatchSchema = z.object({
  kind: z.enum(JOY_WATCH_KINDS),
  params: JoyWatchParamsSchema.default({}),
  notifyEmployee: z.boolean().default(false),
});
export type CreateJoyWatchInput = z.infer<typeof CreateJoyWatchSchema>;

/** الإيقافُ والاستئناف — «انتهت» يكتبها الخادم وحدَه. */
export const UpdateJoyJobSchema = z.object({
  status: z.enum(JOY_JOB_STATUSES).refine((s) => s !== 'DONE', 'لا تُنهى المهمّة يدويًّا.'),
});
export type UpdateJoyJobInput = z.infer<typeof UpdateJoyJobSchema>;
